import React from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { IconFile, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import { searchStore } from "features/ProductSearch/SearchStore";
import Button from "components/Button";

export default observer(function EditorHeader({
  saveAndClose,
  disabled,
}: {
  saveAndClose: (doAdd?: boolean, doMatch?: boolean) => Promise<void>;
  disabled?: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [saveLoading, setSaveLoading] = React.useState<boolean>(false);

  const buttonItems = [
    {
      hidden: searchStore.usingOrganisationSearch,
      name: t("productEditor.saveAndMapp"),
      onClick: () => onClickMenu(t("productEditor.saveAndMapp"), false, true),
      disabled: disabled,
    },
    {
      name: t("commons.save"),
      onClick: () => onClickMenu(t("commons.save")),
      disabled: disabled,
    },
    {
      name: t("commons.cancel"),
      onClick: () => navigate(-1),
    },
  ];

  const filteredButtonItems = buttonItems.filter((item) => !item.hidden);

  const [clickedButton, setClickedButton] = React.useState<string>(
    filteredButtonItems[0].name
  );

  const onClickMenu = async (
    menuName: string,
    doAdd?: boolean,
    doMatch?: boolean
  ) => {
    setSaveLoading(true);
    setClickedButton(menuName);
    await saveAndClose(doAdd, doMatch);
    setSaveLoading(false);
  };

  const cancelButton = () => {
    return (
      <div
        onClick={() => {
          navigate(-1);
          searchStore.setOpenEPDSearch({ state: true, term: "" });
        }}
        className={clsx(
          "border border-gray-300 rounded-md min-w-10 flex items-center justify-center",
          "ml-3 hover:bg-gray-100 text-gray-700 hover:text-gray-900 cursor-pointer !h-9"
        )}
      >
        <IconX className="min-w-6 min-h-6" stroke={2} />
      </div>
    );
  };

  return (
    <div
      className={clsx(
        "flex w-full px-5 py-3 items-center justify-between border-b border-gray-100 bg-pink-50"
      )}
    >
      <div id="editor-title" className="flex">
        <div className="flex justify-between items-center">
          <IconFile
            stroke={3}
            className="bg-pink-300 text-pink-900 p-1 rounded-full mr-[10px] min-w-[24px]"
          />
          <div className="text-xl font-semibold text-black mr-6">
            {t("EPDEditor.title")}
          </div>
        </div>
      </div>
      <div className="flex items-center">
        <Button
          onClick={() => onClickMenu(t("commons.save"))}
          disable={disabled || saveLoading}
          tooltip={disabled ? t("productEditor.saveButtonTooltip") : ""}
          loading={saveLoading}
          className="!h-9"
        >
          {t("commons.save")}
        </Button>
        {cancelButton()}
      </div>
    </div>
  );
});
